import { useCallback, useEffect, useMemo, useState } from "react";
import "../incident.css";
import { getRestaurantList } from "../../services/restaurant";
import {
  ChangeRequestStatus, IChangeRequestQueue, IIncident, IPool, IncidentStatus, REPORTER_LABEL, REQUEST_STATUS_LABEL,
  STATUS_META, typeIcon, typeLabel,
} from "../models/IIncident";
import {
  addPoolMembers, getChangeRequests, getPool, getRestaurantIncidents, incidentError, incidentErrorCode, removePoolMembers,
} from "../services/incidentService";
import { StatusBadge, formatDateTime } from "../components/IncidentBadges";
import IncidentDetailModal from "../components/IncidentDetailModal";

/** Sayfada kullanılan restoran özeti (API'de id opsiyonel; id'siz kayıtlar atlanır) */
interface Rest { id: string; restourantName: string }
type Tab = "incidents" | "requests" | "pool";
interface Summary { pending: number; total: number; poolMember: boolean }

const PAGE_SIZE = 20;
const STATUS_FILTERS: (IncidentStatus | "")[] = ["", "PendingConfirmation", "Confirmed", "Disputed", "Rejected", "Removed"];
const REQUEST_FILTERS: (ChangeRequestStatus | "")[] = ["Open", "Approved", "Rejected", "Withdrawn", ""];

/** Restoran başına özet çağrıları aynı anda en fazla `limit` tane gitsin */
async function mapLimited<T, R>(items: T[], limit: number, fn: (t: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

function Empty({ icon, text }: { icon: string; text: string }) {
  return (
    <div style={{ textAlign: "center", padding: "36px 16px", color: "#9ca3af", fontSize: ".86rem" }}>
      <i className={`bi ${icon}`} style={{ fontSize: "1.8rem", display: "block", marginBottom: 6 }}></i>{text}
    </div>
  );
}

function Spinner() {
  return <div style={{ textAlign: "center", padding: 28 }}><span className="spinner-border spinner-border-sm" style={{ color: "#dc2626" }} role="status" /></div>;
}

export default function IncidentManagementPage() {
  const [tab, setTab] = useState<Tab>("incidents");
  const [restaurants, setRestaurants] = useState<Rest[]>([]);
  const [summaries, setSummaries] = useState<Record<string, Summary>>({});
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [openRequestCount, setOpenRequestCount] = useState(0);
  const [detail, setDetail] = useState<{ id: string; restaurantId: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const nameOf = useCallback(
    (id: string) => restaurants.find((r) => r.id === id)?.restourantName ?? "Restoran",
    [restaurants],
  );

  // Restoranlar + her biri için bekleyen/toplam sayısı (pageSize=1 ile yalnız sayılar)
  const loadSummaries = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await getRestaurantList();
      const list: Rest[] = (res.data?.items ?? []).flatMap((r) => (r.id ? [{ id: r.id, restourantName: r.restourantName ?? "" }] : []));
      setRestaurants(list);
      const results = await mapLimited(list, 6, async (r) => {
        try {
          const s = await getRestaurantIncidents(r.id, undefined, 0, 1);
          return [r.id, { pending: s.data.pendingCount, total: s.data.totalCount, poolMember: s.data.poolMember }] as const;
        } catch (e) {
          if (incidentErrorCode(e) === "CustomerIncidentsDisabled") setDisabled(true);
          return null;
        }
      });
      const map: Record<string, Summary> = {};
      results.forEach((x) => { if (x) map[x[0]] = x[1]; });
      setSummaries(map);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadOpenRequests = useCallback(() => {
    getChangeRequests("Open", 0, 1).then((r) => setOpenRequestCount(r.data.totalCount)).catch(() => {});
  }, []);

  useEffect(() => { loadSummaries(); loadOpenRequests(); }, [loadSummaries, loadOpenRequests]);

  const onChanged = () => { setRefreshKey((k) => k + 1); loadSummaries(); loadOpenRequests(); };

  const totals = useMemo(() => {
    const v = Object.values(summaries);
    return {
      total: v.reduce((a, s) => a + s.total, 0),
      pending: v.reduce((a, s) => a + s.pending, 0),
      pool: v.filter((s) => s.poolMember).length,
    };
  }, [summaries]);

  return (
    <div className="inc-page">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h4 style={{ fontWeight: 800, color: "#111827", marginBottom: 4 }}>
            <i className="bi bi-flag-fill me-2" style={{ color: "#dc2626" }}></i>Olay Bildirimi
          </h4>
          <p style={{ color: "#6b7280", fontSize: ".88rem", margin: 0 }}>
            Restoranların müşteriler hakkında açtığı bildirimleri inceleyin, talepleri karara bağlayın ve paylaşım havuzunu yönetin.
          </p>
        </div>
        <button className="inc-btn inc-btn--sm" onClick={onChanged} disabled={summaryLoading}>
          {summaryLoading ? <span className="spinner-border spinner-border-sm" role="status" /> : <i className="bi bi-arrow-clockwise"></i>} Yenile
        </button>
      </div>

      {disabled && (
        <div className="inc-alert inc-alert--warn" style={{ marginTop: 14 }}>
          <i className="bi bi-toggle-off"></i>
          <div>Olay bildirimi özelliği bu ortamda kapalı (CustomerIncidentsDisabled). Backend'de açılınca veriler görünecek.</div>
        </div>
      )}

      <div className="inc-stats" style={{ marginTop: 16 }}>
        <div className="inc-stat">
          <div className="inc-stat__label">Toplam bildirim</div>
          <div className="inc-stat__value">{summaryLoading ? "…" : totals.total}</div>
        </div>
        <div className="inc-stat">
          <div className="inc-stat__label">Restoran onayı bekleyen</div>
          <div className="inc-stat__value" style={{ color: totals.pending ? "#b45309" : undefined }}>{summaryLoading ? "…" : totals.pending}</div>
        </div>
        <div className="inc-stat" style={{ cursor: "pointer" }} onClick={() => setTab("requests")}>
          <div className="inc-stat__label">Açık talep</div>
          <div className="inc-stat__value" style={{ color: openRequestCount ? "#dc2626" : undefined }}>{openRequestCount}</div>
        </div>
        <div className="inc-stat" style={{ cursor: "pointer" }} onClick={() => setTab("pool")}>
          <div className="inc-stat__label">Havuzdaki restoran</div>
          <div className="inc-stat__value">{summaryLoading ? "…" : totals.pool}</div>
        </div>
      </div>

      <div className="inc-tabs">
        <button className={`inc-tab${tab === "incidents" ? " inc-tab--active" : ""}`} onClick={() => setTab("incidents")}>
          <i className="bi bi-list-ul"></i>Bildirimler
        </button>
        <button className={`inc-tab${tab === "requests" ? " inc-tab--active" : ""}`} onClick={() => setTab("requests")}>
          <i className="bi bi-inboxes"></i>Talepler
          {openRequestCount > 0 && <span className="inc-tab__count">{openRequestCount}</span>}
        </button>
        <button className={`inc-tab${tab === "pool" ? " inc-tab--active" : ""}`} onClick={() => setTab("pool")}>
          <i className="bi bi-share"></i>Paylaşım Havuzu
        </button>
      </div>

      {tab === "incidents" && (
        <IncidentsTab
          restaurants={restaurants}
          summaries={summaries}
          loading={summaryLoading}
          refreshKey={refreshKey}
          onOpen={(i) => setDetail({ id: i.id, restaurantId: i.restourantId })}
        />
      )}
      {tab === "requests" && (
        <RequestsTab nameOf={nameOf} refreshKey={refreshKey} onOpen={(i) => setDetail({ id: i.id, restaurantId: i.restourantId })} />
      )}
      {tab === "pool" && <PoolTab restaurants={restaurants} onChanged={onChanged} />}

      {detail && (
        <IncidentDetailModal
          incidentId={detail.id}
          restaurantName={nameOf(detail.restaurantId)}
          onClose={() => setDetail(null)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

/* ───────────────────────── Bildirimler ───────────────────────── */

function IncidentsTab({ restaurants, summaries, loading, refreshKey, onOpen }: {
  restaurants: Rest[];
  summaries: Record<string, Summary>;
  loading: boolean;
  refreshKey: number;
  onOpen: (i: IIncident) => void;
}) {
  const [selected, setSelected] = useState<string>("");
  const [search, setSearch] = useState("");
  const [onlyWithIncidents, setOnlyWithIncidents] = useState(true);
  const [status, setStatus] = useState<IncidentStatus | "">("");
  const [items, setItems] = useState<IIncident[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sorted = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    return restaurants
      .filter((r) => !q || r.restourantName?.toLocaleLowerCase("tr").includes(q))
      .filter((r) => !onlyWithIncidents || (summaries[r.id]?.total ?? 0) > 0)
      .sort((a, b) =>
        (summaries[b.id]?.pending ?? 0) - (summaries[a.id]?.pending ?? 0)
        || (summaries[b.id]?.total ?? 0) - (summaries[a.id]?.total ?? 0)
        || (a.restourantName ?? "").localeCompare(b.restourantName ?? "", "tr"));
  }, [restaurants, summaries, search, onlyWithIncidents]);

  // İlk açılışta en çok bildirimi olan restoranı seç
  useEffect(() => {
    if (!selected && !loading && sorted.length > 0) setSelected(sorted[0].id);
  }, [selected, loading, sorted]);

  const load = useCallback((p: number) => {
    if (!selected) return;
    setListLoading(true);
    setError(null);
    getRestaurantIncidents(selected, status || undefined, p, PAGE_SIZE)
      .then((res) => { setItems(res.data.items); setTotal(res.data.totalCount); setPage(p); })
      .catch((e) => { setItems([]); setError(incidentError(e, "Bildirimler yüklenemedi.")); })
      .finally(() => setListLoading(false));
  }, [selected, status]);

  useEffect(() => { load(0); }, [load, refreshKey]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const sel = restaurants.find((r) => r.id === selected);
  const selSummary = summaries[selected];

  return (
    <div className="inc-layout">
      {/* Restoranlar */}
      <div className="inc-card">
        <div style={{ padding: 10, borderBottom: "1.5px solid #f1f5f9", display: "flex", flexDirection: "column", gap: 8 }}>
          <input className="inc-input" placeholder="Restoran ara…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ padding: "8px 10px", fontSize: ".84rem" }} />
          <label style={{ fontSize: ".76rem", color: "#4b5563", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={onlyWithIncidents} onChange={(e) => setOnlyWithIncidents(e.target.checked)} />
            Yalnız bildirimi olanlar
          </label>
        </div>
        <div className="inc-rest-list">
          {loading ? <Spinner /> : sorted.length === 0 ? (
            <Empty icon="bi-shop" text={onlyWithIncidents ? "Bildirimi olan restoran yok." : "Restoran bulunamadı."} />
          ) : sorted.map((r) => {
            const s = summaries[r.id];
            return (
              <button key={r.id} className={`inc-rest${selected === r.id ? " inc-rest--active" : ""}`} onClick={() => { setSelected(r.id); }}>
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {s?.poolMember && <i className="bi bi-share-fill me-1" style={{ color: "#7c3aed", fontSize: ".72rem" }} title="Paylaşım havuzunda"></i>}
                  {r.restourantName}
                </span>
                {s && s.pending > 0 && <span className="inc-badge" style={{ background: "#fef3c7", color: "#b45309" }}>{s.pending}</span>}
                <span className="inc-badge" style={{ background: "#f1f5f9", color: "#475569" }}>{s?.total ?? "–"}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Seçilen restoranın bildirimleri */}
      <div className="inc-card" style={{ padding: 14, minWidth: 0 }}>
        {!selected ? (
          <Empty icon="bi-hand-index" text="Soldan bir restoran seçin." />
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", flex: 1, minWidth: 0 }}>{sel?.restourantName}</div>
              {selSummary && (
                <span className="inc-badge" style={selSummary.poolMember ? { background: "#ede9fe", color: "#7c3aed" } : { background: "#f1f5f9", color: "#6b7280" }}>
                  <i className="bi bi-share"></i>{selSummary.poolMember ? "Havuzda" : "Havuz dışı"}
                </span>
              )}
            </div>
            <div className="inc-toolbar">
              {STATUS_FILTERS.map((f) => (
                <button key={f || "all"} className={`inc-chip${status === f ? " inc-chip--active" : ""}`} onClick={() => setStatus(f)}>
                  {f ? <><i className={`bi ${STATUS_META[f].icon}`} style={{ color: STATUS_META[f].color }}></i>{STATUS_META[f].label}</> : "Tümü"}
                </button>
              ))}
            </div>
            {error && <div className="inc-alert inc-alert--error" style={{ marginBottom: 10 }}><i className="bi bi-exclamation-circle-fill"></i><div>{error}</div></div>}
            {listLoading ? <Spinner /> : items.length === 0 ? (
              <Empty icon="bi-inbox" text="Bu filtrede bildirim yok." />
            ) : (
              <div className="inc-list">
                {items.map((i) => (
                  <div key={i.id} className="inc-item inc-item--clickable" onClick={() => onOpen(i)} role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter") onOpen(i); }}>
                    <div className="inc-item__top">
                      <i className={`bi ${typeIcon(i.type)}`} style={{ color: "#dc2626" }}></i>
                      <span className="inc-item__title">{typeLabel(i.type)}</span>
                      <StatusBadge status={i.status} />
                      {i.orderCode && <span style={{ marginLeft: "auto", fontSize: ".74rem", fontWeight: 700, color: "#6b7280" }}>#{i.orderCode}</span>}
                    </div>
                    {i.description && <div className="inc-item__desc" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{i.description}</div>}
                    <div className="inc-item__meta">
                      <span><i className="bi bi-person me-1"></i>{REPORTER_LABEL[i.createdByKind] ?? i.createdByKind}</span>
                      <span><i className="bi bi-clock me-1"></i>{formatDateTime(i.createdAtUtc)}</span>
                      {i.hasEvidence && <span><i className="bi bi-image me-1"></i>Görsel</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {pageCount > 1 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                <button className="inc-btn inc-btn--sm" disabled={page === 0 || listLoading} onClick={() => load(page - 1)}><i className="bi bi-chevron-left"></i></button>
                <span style={{ fontSize: ".78rem", color: "#6b7280" }}>{page + 1} / {pageCount} · {total} kayıt</span>
                <button className="inc-btn inc-btn--sm" disabled={page + 1 >= pageCount || listLoading} onClick={() => load(page + 1)}><i className="bi bi-chevron-right"></i></button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────── Talepler ───────────────────────── */

function RequestsTab({ nameOf, refreshKey, onOpen }: { nameOf: (id: string) => string; refreshKey: number; onOpen: (i: IIncident) => void }) {
  const [status, setStatus] = useState<ChangeRequestStatus | "">("Open");
  const [data, setData] = useState<IChangeRequestQueue | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((p: number) => {
    setLoading(true);
    setError(null);
    getChangeRequests(status || undefined, p, PAGE_SIZE)
      .then((res) => { setData(res.data); setPage(p); })
      .catch((e) => { setData(null); setError(incidentError(e, "Talepler yüklenemedi.")); })
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(0); }, [load, refreshKey]);

  const pageCount = Math.max(1, Math.ceil((data?.totalCount ?? 0) / PAGE_SIZE));

  return (
    <div className="inc-card" style={{ padding: 14 }}>
      <div className="inc-toolbar">
        {REQUEST_FILTERS.map((f) => (
          <button key={f || "all"} className={`inc-chip${status === f ? " inc-chip--active" : ""}`} onClick={() => setStatus(f)}>
            {f ? REQUEST_STATUS_LABEL[f] : "Tümü"}
          </button>
        ))}
        <span className="inc-hint" style={{ marginTop: 0, marginLeft: "auto" }}>En eski talep en üstte</span>
      </div>
      {error && <div className="inc-alert inc-alert--error" style={{ marginBottom: 10 }}><i className="bi bi-exclamation-circle-fill"></i><div>{error}</div></div>}
      {loading ? <Spinner /> : !data || data.items.length === 0 ? (
        <Empty icon="bi-inboxes" text={status === "Open" ? "Karar bekleyen talep yok." : "Talep yok."} />
      ) : (
        <div className="inc-list">
          {data.items.map(({ request: r, incident: i }) => (
            <div key={r.id} className="inc-item inc-item--clickable" onClick={() => onOpen(i)} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter") onOpen(i); }}>
              <div className="inc-item__top">
                <span className="inc-badge" style={r.kind === "Delete" ? { background: "#fee2e2", color: "#991b1b" } : { background: "#e0e7ff", color: "#3730a3" }}>
                  <i className={`bi ${r.kind === "Delete" ? "bi-trash3" : "bi-pencil-square"}`}></i>{r.kind === "Delete" ? "Silme talebi" : "Düzeltme talebi"}
                </span>
                <span className="inc-item__title" style={{ fontSize: ".84rem" }}>{nameOf(i.restourantId)}</span>
                {r.status !== "Open" && <span className="inc-badge" style={{ background: "#f1f5f9", color: "#475569" }}>{REQUEST_STATUS_LABEL[r.status]}</span>}
                {i.orderCode && <span style={{ marginLeft: "auto", fontSize: ".74rem", fontWeight: 700, color: "#6b7280" }}>#{i.orderCode}</span>}
              </div>
              <div className="inc-item__desc">
                <strong>{typeLabel(i.type)}</strong>
                {r.kind === "Correct" && r.proposedType && r.proposedType !== i.type && <> → <strong>{typeLabel(r.proposedType)}</strong></>}
                <div style={{ color: "#6b7280", marginTop: 2 }}>Gerekçe: {r.reason}</div>
              </div>
              <div className="inc-item__meta">
                <span><i className="bi bi-clock me-1"></i>{formatDateTime(r.requestedAtUtc)}</span>
                <StatusBadge status={i.status} />
              </div>
            </div>
          ))}
        </div>
      )}
      {pageCount > 1 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
          <button className="inc-btn inc-btn--sm" disabled={page === 0 || loading} onClick={() => load(page - 1)}><i className="bi bi-chevron-left"></i></button>
          <span style={{ fontSize: ".78rem", color: "#6b7280" }}>{page + 1} / {pageCount}</span>
          <button className="inc-btn inc-btn--sm" disabled={page + 1 >= pageCount || loading} onClick={() => load(page + 1)}><i className="bi bi-chevron-right"></i></button>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Paylaşım havuzu ───────────────────────── */

function PoolTab({ restaurants, onChanged }: { restaurants: Rest[]; onChanged: () => void }) {
  const [pool, setPool] = useState<IPool | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toAdd, setToAdd] = useState<Set<string>>(new Set());
  const [toRemove, setToRemove] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getPool()
      .then((res) => setPool(res.data))
      .catch((e) => setError(incidentError(e, "Havuz yüklenemedi.")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const memberIds = useMemo(() => new Set(pool?.members.map((m) => m.restourantId) ?? []), [pool]);
  const candidates = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    return restaurants
      .filter((r) => !memberIds.has(r.id))
      .filter((r) => !q || r.restourantName?.toLocaleLowerCase("tr").includes(q))
      .sort((a, b) => (a.restourantName ?? "").localeCompare(b.restourantName ?? "", "tr"));
  }, [restaurants, memberIds, search]);

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  };

  const apply = async (kind: "add" | "remove") => {
    const ids = Array.from(kind === "add" ? toAdd : toRemove);
    if (ids.length === 0) return;
    if (kind === "remove" && !window.confirm(`${ids.length} restoran havuzdan çıkarılsın mı? Bu restoranlar artık diğerlerinin bildirimlerini göremez.`)) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = kind === "add" ? await addPoolMembers(ids) : await removePoolMembers(ids);
      setPool(res.data);
      kind === "add" ? setToAdd(new Set()) : setToRemove(new Set());
      setSuccess(kind === "add" ? `${ids.length} restoran havuza eklendi.` : `${ids.length} restoran havuzdan çıkarıldı.`);
      onChanged();
    } catch (e) {
      setError(incidentError(e, "Havuz güncellenemedi."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {pool && (
        <div className={`inc-alert ${pool.isActive ? "inc-alert--success" : "inc-alert--warn"}`}>
          <i className={`bi ${pool.isActive ? "bi-check-circle-fill" : "bi-exclamation-triangle-fill"}`}></i>
          <div>
            <strong>{pool.isActive ? "Paylaşım aktif." : "Paylaşım henüz aktif değil."}</strong>{" "}
            Havuzda {pool.memberCount} restoran var; paylaşımın çalışması için en az {pool.minimumMembers} gerekir.
            <div style={{ marginTop: 2, opacity: .85 }}>
              Havuzdaki restoranlar birbirlerinin onaylı bildirimlerini müşteri risk rozeti olarak (yalnız tür ve tarih) görür. Açıklamalar paylaşılmaz.
            </div>
          </div>
        </div>
      )}
      {error && <div className="inc-alert inc-alert--error"><i className="bi bi-exclamation-circle-fill"></i><div>{error}</div></div>}
      {success && <div className="inc-alert inc-alert--success"><i className="bi bi-check2"></i><div>{success}</div></div>}

      <div className="inc-layout" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))" }}>
        {/* Üyeler */}
        <div className="inc-card">
          <div style={{ padding: "12px 14px", borderBottom: "1.5px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontWeight: 800, flex: 1 }}><i className="bi bi-share-fill me-2" style={{ color: "#7c3aed" }}></i>Havuzdakiler ({pool?.memberCount ?? 0})</div>
            <button className="inc-btn inc-btn--sm inc-btn--ghost-danger" disabled={toRemove.size === 0 || saving} onClick={() => apply("remove")}>
              <i className="bi bi-dash-circle"></i> Çıkar{toRemove.size ? ` (${toRemove.size})` : ""}
            </button>
          </div>
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            {!pool || pool.members.length === 0 ? <Empty icon="bi-share" text="Havuzda restoran yok." /> : pool.members.map((m) => (
              <label key={m.restourantId} className="inc-pool-row" style={{ cursor: "pointer" }}>
                <input type="checkbox" checked={toRemove.has(m.restourantId)} onChange={() => toggle(toRemove, setToRemove, m.restourantId)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: ".86rem", fontWeight: 700, color: "#111827" }}>{m.restourantName}</div>
                  <div style={{ fontSize: ".7rem", color: "#9ca3af" }}>Eklendi: {formatDateTime(m.addedAtUtc)}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Eklenebilecekler */}
        <div className="inc-card">
          <div style={{ padding: "12px 14px", borderBottom: "1.5px solid #f1f5f9", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontWeight: 800, flex: 1 }}><i className="bi bi-shop me-2" style={{ color: "#6b7280" }}></i>Havuz dışındakiler</div>
              <button className="inc-btn inc-btn--sm inc-btn--success" disabled={toAdd.size === 0 || saving} onClick={() => apply("add")}>
                {saving ? <span className="spinner-border spinner-border-sm" role="status" /> : <><i className="bi bi-plus-circle"></i> Ekle{toAdd.size ? ` (${toAdd.size})` : ""}</>}
              </button>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <input className="inc-input" placeholder="Restoran ara…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ padding: "7px 10px", fontSize: ".82rem" }} />
              {candidates.length > 0 && (
                <button className="inc-btn inc-btn--sm" onClick={() => setToAdd(toAdd.size === candidates.length ? new Set() : new Set(candidates.map((c) => c.id)))}>
                  {toAdd.size === candidates.length ? "Hiçbiri" : "Tümü"}
                </button>
              )}
            </div>
          </div>
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            {candidates.length === 0 ? <Empty icon="bi-check2-all" text="Eklenecek restoran yok." /> : candidates.map((r) => (
              <label key={r.id} className="inc-pool-row" style={{ cursor: "pointer" }}>
                <input type="checkbox" checked={toAdd.has(r.id)} onChange={() => toggle(toAdd, setToAdd, r.id)} />
                <span style={{ fontSize: ".86rem", color: "#374151" }}>{r.restourantName}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
